import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import { 
  ArrowLeft, 
  Calendar, 
  Upload, 
  Send, 
  FileText, 
  User,
  CheckCircle2,
  Clock,
  MessageSquare,
  BookOpen,
  Paperclip,
  X,
  Download,
  Users,
  ExternalLink,
  Trash2,
  Edit,
  Lock,
  Unlock,
  FileSearch,
  CheckSquare
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { Progress } from "@/components/ui/progress";

export default function AssignmentDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [assignment, setAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [students, setStudents] = useState({});
  const [allStudents, setAllStudents] = useState([]);
  const [mySubmission, setMySubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ percent: 0, status: '' });
  const [highlightedSubmission, setHighlightedSubmission] = useState(null);
  const fileInputRef = useRef(null);
  const submissionRefs = useRef({});
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    scripture_reference: '',
    due_date: '',
    status: 'active'
  });
  
  // Form states
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [grade, setGrade] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [quickComplete, setQuickComplete] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  // Handle highlight from notification
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && !loading && submissions.length > 0) {
      // Use retry mechanism for DOM elements
      const tryScrollAndHighlight = (attempts = 0) => {
        const ref = submissionRefs.current[highlightId];
        if (ref) {
          setHighlightedSubmission(highlightId);
          ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Remove highlight after animation
          setTimeout(() => setHighlightedSubmission(null), 2500);
        } else if (attempts < 5) {
          // Retry after a short delay if element not ready
          setTimeout(() => tryScrollAndHighlight(attempts + 1), 200);
        }
      };
      
      // Start with initial delay to allow DOM to render
      const timer = setTimeout(() => tryScrollAndHighlight(), 300);
      return () => clearTimeout(timer);
    }
  }, [searchParams, loading, submissions]);

  const fetchData = async () => {
    try {
      const [assignmentRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/assignments/${id}`),
        axios.get(`${API}/submissions/assignment/${id}`)
      ]);

      setAssignment(assignmentRes.data);
      setSubmissions(submissionsRes.data);
      
      // Fetch student info for teacher view
      if (user.role === 'teacher') {
        const studentsRes = await axios.get(`${API}/users/students`);
        const studentMap = {};
        studentsRes.data.forEach(s => {
          studentMap[s.user_id] = s;
        });
        setStudents(studentMap);
        
        // If assignment is for individual student, only show that student
        // Otherwise show all students
        if (assignmentRes.data.assigned_to) {
          const assignedStudent = studentsRes.data.find(s => s.user_id === assignmentRes.data.assigned_to);
          setAllStudents(assignedStudent ? [assignedStudent] : []);
        } else {
          setAllStudents(studentsRes.data);
        }
      }
      
      // Find student's own submission
      if (user.role === 'student') {
        const mine = submissionsRes.data.find(s => s.student_id === user.user_id);
        if (mine) {
          setMySubmission(mine);
          setContent(mine.content);
          setAttachments(mine.attachments?.map(a => ({ file_id: a, filename: a })) || []);
        }
      }
    } catch (error) {
      console.error("Error fetching assignment:", error);
      toast.error("Failed to load assignment");
    } finally {
      setLoading(false);
    }
  };

  const deleteAssignment = async () => {
    try {
      await axios.delete(`${API}/assignments/${id}`);
      toast.success("Assignment deleted");
      navigate('/assignments');
    } catch (error) {
      console.error("Error deleting assignment:", error);
      toast.error("Failed to delete assignment");
    }
  };

  const handleEditSave = async () => {
    if (!editForm.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    try {
      await axios.put(`${API}/assignments/${id}`, {
        title: editForm.title,
        description: editForm.description,
        scripture_reference: editForm.scripture_reference || null,
        due_date: editForm.due_date || null,
        assigned_to: assignment.assigned_to,
        attachments: assignment.attachments || [],
        status: editForm.status
      });
      toast.success("Assignment updated successfully");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast.error("Failed to update assignment");
    }
  };

  const openEditDialog = () => {
    setEditForm({
      title: assignment.title || '',
      description: assignment.description || '',
      scripture_reference: assignment.scripture_reference || '',
      due_date: assignment.due_date ? new Date(assignment.due_date).toISOString().split('T')[0] : '',
      status: assignment.status || 'active'
    });
    setEditDialogOpen(true);
  };

  const toggleStatus = async () => {
    const newStatus = assignment.status === 'active' ? 'closed' : 'active';
    try {
      await axios.put(`${API}/assignments/${id}`, {
        title: assignment.title,
        description: assignment.description,
        scripture_reference: assignment.scripture_reference || null,
        due_date: assignment.due_date || null,
        assigned_to: assignment.assigned_to,
        attachments: assignment.attachments || [],
        status: newStatus
      });
      toast.success(`Assignment ${newStatus === 'active' ? 'reopened' : 'closed'}`);
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const getStudentStatus = (studentId) => {
    const submission = submissions.find(s => s.student_id === studentId);
    if (!submission) return { status: 'not_submitted', label: 'Not Submitted', color: 'text-slate-500' };
    if (submission.status === 'graded') return { status: 'graded', label: 'Graded', color: 'text-green-600' };
    if (submission.status === 'submitted') return { status: 'submitted', label: 'Awaiting Review', color: 'text-amber-600' };
    return { status: 'draft', label: 'Draft', color: 'text-slate-500' };
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress({ percent: 10, status: 'Uploading...' });
    const uploadedFiles = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        // Stage 1: Upload file (10-40%)
        setUploadProgress({ percent: 20, status: 'Uploading...' });
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const fileId = response.data.file_id;
        setUploadProgress({ percent: 40, status: 'Uploading...' });

        uploadedFiles.push({
          file_id: fileId,
          filename: response.data.filename,
          size: response.data.size
        });

        // Check if file is PDF or image for OCR
        const ext = file.name.split('.').pop().toLowerCase();
        const isOcrSupported = ['pdf', 'jpg', 'jpeg', 'png'].includes(ext);

        if (isOcrSupported) {
          setUploading(false);
          setExtracting(true);
          
          // Stage 2: Reading document (40-90%)
          setUploadProgress({ percent: 50, status: 'Reading document...' });
          
          // Simulate progress during OCR
          const progressInterval = setInterval(() => {
            setUploadProgress(prev => ({
              percent: Math.min(prev.percent + 5, 85),
              status: 'Reading document...'
            }));
          }, 500);
          
          try {
            const extractResponse = await axios.post(`${API}/extract-text/${fileId}`);
            
            clearInterval(progressInterval);
            setUploadProgress({ percent: 90, status: 'Processing text...' });
            
            if (extractResponse.data.success && extractResponse.data.text) {
              const extractedText = extractResponse.data.text;
              const sourceType = extractResponse.data.source_type === 'pdf' ? 'PDF' : 'image';
              
              // Append extracted text to content with a note
              const separator = content.trim() ? '\n\n' : '';
              const noteText = `${separator}--- Text extracted from ${file.name} (${sourceType}) ---\n${extractedText}`;
              
              setContent(prev => prev + noteText);
              setUploadProgress({ percent: 100, status: 'Complete!' });
              toast.success(`Text extracted from ${file.name}`);
            } else {
              setUploadProgress({ percent: 100, status: 'Complete!' });
            }
          } catch (extractError) {
            clearInterval(progressInterval);
            console.error("Text extraction error:", extractError);
            setUploadProgress({ percent: 100, status: 'Could not read document' });
            toast.error(`Could not extract text from ${file.name}`);
          } finally {
            setExtracting(false);
          }
        } else {
          setUploadProgress({ percent: 100, status: 'Complete!' });
        }
      }

      setAttachments(prev => [...prev, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} file(s) uploaded`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file(s)");
    } finally {
      setUploading(false);
      setExtracting(false);
      
      // Reset progress after a short delay
      setTimeout(() => {
        setUploadProgress({ percent: 0, status: '' });
      }, 1500);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // If not quick complete, require content
    if (!quickComplete && !content.trim()) {
      toast.error("Please enter your submission content or check the quick complete option");
      return;
    }

    setSubmitting(true);
    try {
      const submissionContent = quickComplete && !content.trim() 
        ? "✓ Completed - No additional comments" 
        : content;
      
      await axios.post(`${API}/submissions`, {
        assignment_id: id,
        content: submissionContent,
        attachments: attachments.map(a => a.file_id)
      });

      toast.success("Assignment submitted successfully!");
      fetchData();
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Failed to submit assignment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFeedback = async (submissionId) => {
    if (!feedback.trim()) {
      toast.error("Please enter feedback");
      return;
    }

    try {
      await axios.post(`${API}/submissions/${submissionId}/feedback`, {
        feedback: feedback,
        grade: grade || null
      });

      toast.success("Feedback submitted!");
      setFeedback('');
      setGrade('');
      setSelectedSubmission(null);
      fetchData();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback");
    }
  };

  const handleAcknowledge = async (submissionId) => {
    try {
      await axios.post(`${API}/submissions/${submissionId}/feedback`, {
        feedback: "Acknowledged",
        grade: null
      });
      toast.success("Submission acknowledged");
      setSelectedSubmission(null);
      fetchData();
    } catch (error) {
      console.error("Error acknowledging:", error);
      toast.error("Failed to acknowledge submission");
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!assignment) {
    return (
      <Layout user={user}>
        <div className="text-center py-16">
          <p className="text-slate-600">Assignment not found</p>
          <Button variant="link" onClick={() => navigate('/assignments')}>
            Back to Assignments
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/assignments')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-serif text-2xl font-bold text-slate-900">
              {assignment.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              {assignment.due_date && (
                <span className="text-slate-600 text-sm flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Due: {format(new Date(assignment.due_date), 'MMMM d, yyyy')}
                </span>
              )}
              {assignment.assigned_to ? (
                <Badge variant="outline" className="text-xs">
                  <User className="w-3 h-3 mr-1" />
                  {students[assignment.assigned_to]?.name || 'Individual'}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs">
                  <Users className="w-3 h-3 mr-1" />
                  Whole Class
                </Badge>
              )}
              {assignment.status === 'closed' && (
                <Badge className="bg-slate-500 text-white text-xs">
                  <Lock className="w-3 h-3 mr-1" />
                  Closed
                </Badge>
              )}
            </div>
          </div>
          {user.role === 'teacher' && (
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleStatus}
                className={assignment.status === 'closed' 
                  ? "text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                  : "text-slate-600 hover:text-slate-700"
                }
              >
                {assignment.status === 'closed' ? (
                  <>
                    <Unlock className="w-4 h-4 mr-2" />
                    Reopen
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Close
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={openEditDialog}
                className="text-slate-600 hover:text-slate-700"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Assignment Description */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-lg">Assignment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Scripture Reference */}
                {assignment.scripture_reference && (
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-5 h-5 text-amber-700" />
                      <span className="font-semibold text-amber-800">Scripture Reference</span>
                    </div>
                    <p className="text-amber-900 font-serif text-lg">{assignment.scripture_reference}</p>
                  </div>
                )}

                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {assignment.description}
                </p>

                {/* Assignment Attachments */}
                {assignment.attachments && assignment.attachments.length > 0 && (
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-sm font-medium text-slate-700 mb-2">Attached Resources</p>
                    <div className="space-y-2">
                      {assignment.attachments.map((file, index) => (
                        <a 
                          key={index}
                          href={`${API}/files/${file}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          <Paperclip className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-700 flex-1">{file}</span>
                          <Download className="w-4 h-4 text-slate-400" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Student Submission Form */}
            {user.role === 'student' && (
              <Card className="border-slate-100">
                <CardHeader>
                  <CardTitle className="text-lg">Your Submission</CardTitle>
                  {mySubmission && (
                    <Badge variant={mySubmission.status === 'graded' ? 'default' : 'secondary'}>
                      {mySubmission.status === 'graded' ? 'Graded' : 'Submitted'}
                    </Badge>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Quick Complete Option */}
                  {mySubmission?.status !== 'graded' && (
                    <div 
                      className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                        quickComplete 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setQuickComplete(!quickComplete)}
                    >
                      <Checkbox 
                        id="quick-complete"
                        checked={quickComplete}
                        onCheckedChange={setQuickComplete}
                        className="mt-0.5"
                        data-testid="quick-complete-checkbox"
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor="quick-complete" 
                          className="text-sm font-medium text-slate-900 cursor-pointer flex items-center gap-2"
                        >
                          <CheckSquare className="w-4 h-4 text-green-600" />
                          I&apos;ve completed this and that&apos;s all I have to say about that.
                        </label>
                        <p className="text-xs text-slate-500 mt-1">
                          Check this box to submit without additional comments
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Text area - show as optional when quick complete is checked */}
                  <div className="space-y-2">
                    {!quickComplete && (
                      <Label className="text-sm text-slate-600">Your response</Label>
                    )}
                    {quickComplete && (
                      <Label className="text-sm text-slate-500">Additional comments (optional)</Label>
                    )}
                    <Textarea
                      data-testid="submission-content"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder={quickComplete ? "Add any additional comments here (optional)..." : "Type your assignment response here..."}
                      className={`min-h-[150px] journal-content ${quickComplete ? 'opacity-75' : ''}`}
                      disabled={mySubmission?.status === 'graded'}
                    />
                  </div>

                  {/* File Upload for Submission */}
                  {mySubmission?.status !== 'graded' && (
                    <div className="space-y-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        className="hidden"
                      />
                      
                      {/* Progress Bar - Show when uploading or extracting */}
                      {(uploading || extracting) && uploadProgress.status && (
                        <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600 flex items-center gap-2">
                              {extracting ? (
                                <FileSearch className="w-4 h-4 animate-pulse" />
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                              {uploadProgress.status}
                            </span>
                            <span className="text-slate-500">{uploadProgress.percent}%</span>
                          </div>
                          <Progress value={uploadProgress.percent} className="h-2" />
                          {extracting && (
                            <p className="text-xs text-slate-500">
                              Extracting text from document using AI...
                            </p>
                          )}
                        </div>
                      )}
                      
                      {!uploading && !extracting && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Attach Documents
                        </Button>
                      )}
                      
                      {/* Attached Files List */}
                      {attachments.length > 0 && (
                        <div className="space-y-2">
                          {attachments.map((file, index) => (
                            <div 
                              key={index}
                              className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                            >
                              <a 
                                href={`${API}/files/${file.file_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 min-w-0 hover:text-amber-600 transition-colors"
                              >
                                <Paperclip className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                <span className="text-sm text-slate-700 truncate">{file.filename}</span>
                                <ExternalLink className="w-3 h-3 text-slate-400 flex-shrink-0" />
                              </a>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeAttachment(index)}
                                className="h-8 w-8 p-0 flex-shrink-0"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {mySubmission?.status !== 'graded' && (
                    <div className="flex justify-end">
                      <Button 
                        data-testid="submit-assignment-btn"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="bg-amber-600 hover:bg-amber-700 rounded-full"
                      >
                        {submitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            {mySubmission ? 'Update Submission' : 'Submit Assignment'}
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Show feedback if graded */}
                  {mySubmission?.feedback && (
                    <div className="mt-6 p-4 bg-green-50 rounded-xl border border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-green-800">Teacher Feedback</span>
                        {mySubmission.grade && (
                          <Badge className="ml-auto bg-green-600">
                            Grade: {mySubmission.grade}
                          </Badge>
                        )}
                      </div>
                      <p className="text-green-800">{mySubmission.feedback}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Teacher Submissions View */}
            {user.role === 'teacher' && (
              <div data-testid="submissions-section">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="font-serif text-xl font-semibold text-slate-900">Student Submissions</h2>
                    <p className="text-sm text-slate-500 mt-1">{submissions.length} submission{submissions.length !== 1 ? 's' : ''} received</p>
                  </div>
                  {submissions.length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex items-center gap-1.5 text-amber-600">
                        <Clock className="w-3.5 h-3.5" />
                        {submissions.filter(s => s.status === 'submitted').length} pending
                      </span>
                      <span className="text-slate-300">|</span>
                      <span className="flex items-center gap-1.5 text-green-600">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {submissions.filter(s => s.status === 'graded').length} graded
                      </span>
                    </div>
                  )}
                </div>

                {submissions.length === 0 ? (
                  <Card className="border-slate-100">
                    <CardContent className="py-12 text-center">
                      <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No submissions yet</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {submissions.map((sub) => {
                      const student = students[sub.student_id];
                      const isPending = sub.status === 'submitted';
                      const isGraded = sub.status === 'graded';
                      const studentName = sub.student_name || student?.name || 'Unknown Student';
                      const studentPicture = sub.student_picture || student?.picture;
                      const isHighlighted = highlightedSubmission === sub.submission_id;
                      
                      return (
                        <Card 
                          key={sub.submission_id}
                          ref={el => submissionRefs.current[sub.submission_id] = el}
                          data-testid={`submission-card-${sub.submission_id}`}
                          onClick={() => setSelectedSubmission(sub.submission_id)}
                          className={`overflow-hidden cursor-pointer group transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                            isHighlighted 
                              ? 'notification-highlight ring-2 ring-amber-400 ring-offset-2' 
                              : isPending 
                                ? 'border-amber-200' 
                                : isGraded
                                  ? 'border-green-200/60'
                                  : 'border-slate-100'
                          }`}
                        >
                          <div className={`h-1 ${isPending ? 'bg-amber-400' : isGraded ? 'bg-green-400' : 'bg-slate-200'}`} />
                          <CardContent className="p-4 flex flex-col items-center text-center">
                            <Avatar className="w-12 h-12 mt-1 ring-2 ring-offset-1 ring-slate-100">
                              <AvatarImage src={studentPicture} />
                              <AvatarFallback className={`text-sm font-medium ${isPending ? 'bg-amber-50 text-amber-700' : isGraded ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                                {getInitials(studentName)}
                              </AvatarFallback>
                            </Avatar>
                            <p className="font-semibold text-slate-900 text-sm mt-2.5 truncate w-full">{studentName}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {format(new Date(sub.created_at), 'MMM d, yyyy')}
                            </p>
                            <Badge 
                              data-testid={`submission-status-${sub.submission_id}`}
                              className={`mt-2.5 text-xs ${
                                isPending 
                                  ? 'bg-amber-500 text-white hover:bg-amber-500' 
                                  : isGraded 
                                    ? 'bg-green-100 text-green-800 hover:bg-green-100'
                                    : ''
                              }`}
                              variant={!isPending && !isGraded ? 'outline' : 'default'}
                            >
                              {isPending ? 'Pending' : isGraded ? 'Graded' : sub.status}
                            </Badge>
                            {sub.attachments && sub.attachments.length > 0 && (
                              <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                                <Paperclip className="w-3 h-3" />
                                {sub.attachments.length} file{sub.attachments.length !== 1 ? 's' : ''}
                              </div>
                            )}
                            {sub.content && (
                              <p className="mt-2 text-xs text-slate-500 line-clamp-2 text-left w-full leading-relaxed border-t border-slate-100 pt-2">
                                {sub.content}
                              </p>
                            )}
                            {isPending && (
                              <Button
                                size="sm"
                                data-testid={`acknowledge-btn-${sub.submission_id}`}
                                className="mt-3 bg-green-600 hover:bg-green-700 text-white text-xs rounded-full px-4 h-7"
                                onClick={(e) => { e.stopPropagation(); handleAcknowledge(sub.submission_id); }}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                Acknowledge
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Submission Detail Dialog */}
            {user.role === 'teacher' && (() => {
              const detailSub = submissions.find(s => s.submission_id === selectedSubmission);
              if (!detailSub) return null;
              const student = students[detailSub.student_id];
              const isPending = detailSub.status === 'submitted';
              const isGraded = detailSub.status === 'graded';
              const studentName = detailSub.student_name || student?.name || 'Unknown Student';
              const studentPicture = detailSub.student_picture || student?.picture;
              const studentEmail = student?.email || '';

              return (
                <Dialog open={!!detailSub} onOpenChange={(open) => { if (!open) { setSelectedSubmission(null); setFeedback(''); setGrade(''); } }}>
                  <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="submission-detail-dialog">
                    <DialogHeader>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={studentPicture} />
                          <AvatarFallback className={`text-sm font-medium ${isPending ? 'bg-amber-50 text-amber-700' : isGraded ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                            {getInitials(studentName)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <DialogTitle className="text-left">{studentName}</DialogTitle>
                          <DialogDescription className="text-left">{studentEmail}</DialogDescription>
                        </div>
                        <Badge 
                          className={`ml-auto text-xs ${
                            isPending ? 'bg-amber-500 text-white hover:bg-amber-500' : isGraded ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''
                          }`}
                          variant={!isPending && !isGraded ? 'outline' : 'default'}
                        >
                          {isPending ? 'Awaiting Review' : isGraded ? 'Graded' : detailSub.status}
                        </Badge>
                      </div>
                    </DialogHeader>

                    <div className="space-y-4 mt-2">
                      <p className="text-xs text-slate-400">
                        Submitted {format(new Date(detailSub.created_at), 'MMMM d, yyyy · h:mm a')}
                      </p>

                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                        <p className="text-slate-700 text-sm whitespace-pre-wrap leading-relaxed">
                          {detailSub.content}
                        </p>
                      </div>

                      {detailSub.attachments && detailSub.attachments.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Attachments</p>
                          <div className="flex flex-wrap gap-2">
                            {detailSub.attachments.map((file, idx) => (
                              <a 
                                key={idx} 
                                href={`${API}/files/${file}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`attachment-link-${detailSub.submission_id}-${idx}`}
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 hover:border-amber-300 hover:bg-amber-50/50 rounded-lg text-sm text-slate-600 transition-colors duration-200"
                              >
                                <Paperclip className="w-3.5 h-3.5 text-slate-400" />
                                <span className="truncate max-w-[180px]">{file}</span>
                                <ExternalLink className="w-3 h-3 text-slate-300" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {detailSub.feedback && (
                        <div className="p-4 bg-green-50 rounded-xl border border-green-200/60">
                          <div className="flex items-start gap-3">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold uppercase tracking-wider text-green-700">Your Feedback</span>
                                {detailSub.grade && (
                                  <Badge className="bg-green-600 text-white text-xs">Grade: {detailSub.grade}</Badge>
                                )}
                              </div>
                              <p className="text-sm text-green-800 leading-relaxed">{detailSub.feedback}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {detailSub.status !== 'graded' && (
                        <div className="space-y-3 pt-2 border-t border-slate-100">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Respond</p>
                            <Button
                              size="sm"
                              data-testid="dialog-acknowledge-btn"
                              className="bg-green-600 hover:bg-green-700 text-white rounded-full px-4 h-8"
                              onClick={() => handleAcknowledge(detailSub.submission_id)}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                              Acknowledge
                            </Button>
                          </div>
                          <Separator className="my-1" />
                          <Textarea
                            data-testid="feedback-input"
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            placeholder="Or leave detailed feedback..."
                            className="min-h-[80px]"
                          />
                          <div className="flex items-center gap-2">
                            <Input
                              data-testid="grade-input"
                              value={grade}
                              onChange={(e) => setGrade(e.target.value)}
                              placeholder="Grade (optional)"
                              className="w-32"
                            />
                            <div className="flex-1" />
                            <Button
                              data-testid="submit-feedback-btn"
                              size="sm"
                              onClick={() => handleFeedback(detailSub.submission_id)}
                              className="bg-slate-800 hover:bg-slate-900"
                            >
                              <Send className="w-3.5 h-3.5 mr-1.5" />
                              Submit Feedback
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              );
            })()}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="text-sm">Assignment Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Status</span>
                  <Badge variant="outline">Active</Badge>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-slate-600">Created</span>
                  <span className="text-slate-900">
                    {format(new Date(assignment.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
                {assignment.scripture_reference && (
                  <>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-slate-600">Scripture</span>
                      <span className="text-slate-900 font-serif">{assignment.scripture_reference}</span>
                    </div>
                  </>
                )}
                {user.role === 'teacher' && (
                  <>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-slate-600">Submissions</span>
                      <span className="text-slate-900">{submissions.length}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Student Status List - Teacher Only */}
            {user.role === 'teacher' && allStudents.length > 0 && (
              <Card className="border-slate-100 overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center">
                    {assignment.assigned_to ? (
                      <><User className="w-4 h-4 mr-2" />Assigned Student</>
                    ) : (
                      <><Users className="w-4 h-4 mr-2" />Students ({allStudents.length})</>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[400px]">
                    <div className="p-3 space-y-2">
                      {allStudents.map((student) => {
                        const statusInfo = getStudentStatus(student.user_id);
                        return (
                          <div 
                            key={student.user_id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate(`/students/${student.user_id}`)}
                          >
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={student.picture} />
                              <AvatarFallback className="bg-slate-100 text-slate-700 text-xs">
                                {getInitials(student.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {student.name}
                              </p>
                              <p className={`text-xs ${statusInfo.color}`}>
                                {statusInfo.status === 'graded' && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                                {statusInfo.label}
                              </p>
                            </div>
                            {statusInfo.status === 'submitted' && (
                              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">
                                Review
                              </Badge>
                            )}
                            {statusInfo.status === 'graded' && (
                              <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                                Done
                              </Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={deleteAssignment}
        title="Delete Assignment?"
        description="This will permanently delete this assignment and all student submissions. This action cannot be undone."
        confirmText="Delete Assignment"
      />

      {/* Edit Assignment Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Assignment</DialogTitle>
            <DialogDescription>
              Update the assignment details
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title *</label>
              <Input
                placeholder="Assignment title"
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="Describe the assignment..."
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Scripture Reference</label>
              <Input
                placeholder="e.g., John 3:16"
                value={editForm.scripture_reference}
                onChange={(e) => setEditForm(prev => ({ ...prev, scripture_reference: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Due Date</label>
              <Input
                type="date"
                value={editForm.due_date}
                onChange={(e) => setEditForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={editForm.status}
                onValueChange={(value) => setEditForm(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">
                    <div className="flex items-center gap-2">
                      <Unlock className="w-4 h-4 text-green-600" />
                      Active - Students can submit
                    </div>
                  </SelectItem>
                  <SelectItem value="closed">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-slate-500" />
                      Closed - No new submissions
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditSave} className="bg-slate-800 hover:bg-slate-900">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
